import React, { memo, useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import TabButton from './TabButton';
import DragWrapperTab from './DragWrapperTab';
import { useThrottle } from '../../../utils/hooks/useThrottle';
import Icon from '../Icon';

const TabNav = memo(
    ({
        tabs,
        activeKey,
        position,
        editable,
        alwaysShowCloseTab,
        onTabClick,
        onMouseOver,
        onMouseOut,
        onClickForPopover,
        onClose,
        classes,
        override,
        draggable,
        moveTab,
        findTab,
    }) => {
        const navWrapRef = useRef(null);
        const navScrollRef = useRef(null);
        const navRef = useRef(null);
        const inkBarRef = useRef(null);
        const tabRefs = useRef({});
        const rafIdRef = useRef(null);
        const [showPrev, setShowPrev] = useState(false);
        const [showNext, setShowNext] = useState(false);
        const [canGoPrev, setCanGoPrev] = useState(false);
        const [canGoNext, setCanGoNext] = useState(false);

        const checkOverflowInternal = useCallback(() => {
            if (!navWrapRef.current || !navScrollRef.current) return;

            const wrap = navWrapRef.current;
            const navScroll = navScrollRef.current;

            const wrapWidth = wrap.clientWidth;
            const scrollWidth = navScroll.scrollWidth;
            const overflow = scrollWidth > wrapWidth;
            const scrollLeft = wrap.scrollLeft;
            const maxScroll = scrollWidth - wrapWidth;

            setShowPrev(overflow);
            setShowNext(overflow);
            setCanGoPrev(overflow && scrollLeft > 1);
            setCanGoNext(overflow && scrollLeft < maxScroll - 1);
        }, []);

        const checkOverflow = useThrottle(checkOverflowInternal, 100);

        const updateInkBar = useCallback(() => {
            if (rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current);
            }

            rafIdRef.current = requestAnimationFrame(() => {
                if (!inkBarRef.current) return;

                const inkBar = inkBarRef.current;

                if (!activeKey || !tabRefs.current[activeKey]) {
                    // Hide inkBar when no active tab
                    inkBar.style.width = '0px';
                    return;
                }

                const activeTab = tabRefs.current[activeKey];

                if (!activeTab) {
                    inkBar.style.width = '0px';
                    return;
                }

                const left = activeTab.offsetLeft;
                const width = activeTab.offsetWidth;
                inkBar.style.left = `${left}px`;
                inkBar.style.width = `${width}px`;
            });
        }, [activeKey]);

        const scrollToActiveTab = useCallback(() => {
            if (!navWrapRef.current || !activeKey || !tabRefs.current[activeKey]) return;

            const wrap = navWrapRef.current;
            const activeTab = tabRefs.current[activeKey];

            const tabLeft = activeTab.offsetLeft;
            const tabWidth = activeTab.offsetWidth;
            const wrapWidth = wrap.offsetWidth;
            const scrollLeft = wrap.scrollLeft;

            if (tabLeft < scrollLeft) {
                wrap.scrollLeft = tabLeft;
            } else if (tabLeft + tabWidth > scrollLeft + wrapWidth) {
                wrap.scrollLeft = tabLeft + tabWidth - wrapWidth;
            }
        }, [activeKey]);

        useEffect(() => {
            checkOverflowInternal();
            updateInkBar();
            scrollToActiveTab();

            const wrap = navWrapRef.current;
            const navScroll = navScrollRef.current;

            if (!wrap || !navScroll) return;

            // Use ResizeObserver for better performance
            const resizeObserver = new ResizeObserver(() => {
                checkOverflow();
                updateInkBar();
            });

            resizeObserver.observe(wrap);
            resizeObserver.observe(navScroll);

            // Scroll handler with throttling
            const handleScroll = () => {
                checkOverflow();
            };

            wrap.addEventListener('scroll', handleScroll, { passive: true });

            return () => {
                resizeObserver.disconnect();
                wrap.removeEventListener('scroll', handleScroll);
                if (rafIdRef.current) {
                    cancelAnimationFrame(rafIdRef.current);
                }
            };
        }, [
            activeKey,
            tabs,
            checkOverflow,
            checkOverflowInternal,
            updateInkBar,
            scrollToActiveTab,
        ]);

        const handleKeyDown = useCallback(
            (e) => {
                if (!tabs.length) return;

                const currentIndex = tabs.findIndex((tab) => tab.key === activeKey);
                let nextIndex = currentIndex;

                if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                    e.preventDefault();
                    nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
                } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
                } else if (e.key === 'Home') {
                    e.preventDefault();
                    nextIndex = 0;
                } else if (e.key === 'End') {
                    e.preventDefault();
                    nextIndex = tabs.length - 1;
                }

                if (nextIndex !== currentIndex) {
                    onTabClick(tabs[nextIndex].key);
                    // Focus the new tab
                    setTimeout(() => {
                        if (tabRefs.current[tabs[nextIndex].key]) {
                            tabRefs.current[tabs[nextIndex].key].focus();
                        }
                    }, 0);
                }
            },
            [tabs, activeKey, onTabClick],
        );

        const handlePrevClick = () => {
            if (!navWrapRef.current) return;
            const wrap = navWrapRef.current;
            const scrollAmount = wrap.offsetWidth * 0.8;

            wrap.scrollLeft = Math.max(0, wrap.scrollLeft - scrollAmount);
        };

        const handleNextClick = () => {
            if (!navWrapRef.current || !navScrollRef.current) return;
            const wrap = navWrapRef.current;
            const navScroll = navScrollRef.current;
            const scrollAmount = wrap.offsetWidth * 0.8;

            const maxScroll = navScroll.scrollWidth - wrap.offsetWidth;
            wrap.scrollLeft = Math.min(maxScroll, wrap.scrollLeft + scrollAmount);
        };

        const navContainerClassName = classes.navContainer;

        const prevClassName = classnames(classes.navArrowBase, classes.navPrev, {
            [classes.navArrowShow]: showPrev,
            [classes.navBtnDisabled]: !canGoPrev,
        });

        const nextClassName = classnames(classes.navArrowBase, classes.navNext, {
            [classes.navArrowShow]: showNext,
            [classes.navBtnDisabled]: !canGoNext,
        });

        const renderTabButton = useCallback(
            (tab) => {
                const { key, title, fixed, popoverContent, popoverWidth } = tab;
                const showClose = editable && (tabs.length > 1 || alwaysShowCloseTab);

                const button = (
                    <TabButton
                        ref={(el) => {
                            if (el) tabRefs.current[key] = el;
                        }}
                        key={key}
                        tabKey={key}
                        title={title}
                        active={activeKey === key}
                        onClick={onTabClick}
                        onMouseOver={onMouseOver}
                        onMouseOut={onMouseOut}
                        onClickForPopover={onClickForPopover}
                        fixed={fixed}
                        editable={editable}
                        showClose={showClose}
                        onClose={onClose}
                        popoverContent={popoverContent}
                        popoverWidth={popoverWidth}
                        classes={classes}
                        override={override}
                    />
                );

                if (draggable) {
                    return (
                        <DragWrapperTab key={key} id={key} moveTab={moveTab} findTab={findTab}>
                            {button}
                        </DragWrapperTab>
                    );
                }

                return button;
            },
            [
                activeKey,
                alwaysShowCloseTab,
                classes,
                draggable,
                editable,
                findTab,
                moveTab,
                onClose,
                onClickForPopover,
                onMouseOut,
                onMouseOver,
                onTabClick,
                override,
                tabs.length,
            ],
        );

        const navClassName = classnames(classes.nav, classes.navAnimated);
        const navScrollClassName = classnames(classes.navScroll, {
            [classes.navScrollWithPadding]: showPrev || showNext,
        });

        return (
            <div className={navContainerClassName}>
                <div className={prevClassName} onClick={canGoPrev ? handlePrevClick : undefined}>
                    <Icon name="chevronLeft" className={classes.navArrowIcon} />
                </div>
                <div className={classes.navWrap} ref={navWrapRef}>
                    <div className={navScrollClassName} ref={navScrollRef}>
                        <div className={classes.navSwipe}>
                            <div
                                className={navClassName}
                                role="tablist"
                                ref={navRef}
                                onKeyDown={handleKeyDown}
                                tabIndex={-1}
                            >
                                <div>{tabs.map(renderTabButton)}</div>
                                <div className={classes.inkBar} ref={inkBarRef} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={nextClassName} onClick={canGoNext ? handleNextClick : undefined}>
                    <Icon name="chevronRight" className={classes.navArrowIcon} />
                </div>
            </div>
        );
    },
);

TabNav.propTypes = {
    /** Array of tab configuration objects */
    tabs: PropTypes.array.isRequired,
    /** Key of the currently active tab */
    activeKey: PropTypes.string,
    /** Position of tabs relative to content */
    position: PropTypes.string,
    /** When true, enables editing mode with close buttons */
    editable: PropTypes.bool,
    /** When true, always shows close button even with single tab */
    alwaysShowCloseTab: PropTypes.bool,
    /** Callback when a tab is clicked */
    onTabClick: PropTypes.func.isRequired,
    /** Callback when mouse enters a tab */
    onMouseOver: PropTypes.func,
    /** Callback when mouse leaves a tab */
    onMouseOut: PropTypes.func,
    /** Callback for popover click interaction */
    onClickForPopover: PropTypes.func,
    /** Callback when a tab is closed */
    onClose: PropTypes.func,
    /** Classes object provided by the styling system */
    classes: PropTypes.object,
    /** Override object for styling inner elements */
    override: PropTypes.object,
    /** When true, enables drag and drop reordering */
    draggable: PropTypes.bool,
    /** Function to move a tab to a new position */
    moveTab: PropTypes.func,
    /** Function to find a tab by key */
    findTab: PropTypes.func,
};

export default TabNav;
